// ── Platform detection ───────────────────────────────────────────────────────
//
// Used to label keyboard shortcuts with the key names the user actually has.
// "Enter" and "Shift+Enter" are wrong on macOS and iPadOS, where the keys are
// labelled Return and ⇧.
//
// Prefers `navigator.userAgentData.platform`, which is the modern, non-deprecated
// source, and falls back to `navigator.platform`. iPadOS 13+ deliberately reports
// itself as "MacIntel" to serve desktop sites — which is fine here, since we only
// care whether the keyboard says Return.

/**
 * True when the user is on a Mac, iPhone, iPad or iPod.
 *
 * SSR-safe: returns false when there is no `navigator`, so a server render
 * produces the non-Apple string. Components must therefore resolve this AFTER
 * mount (see useIsApplePlatform) rather than during the first render, or the
 * markup would differ between server and client.
 */
export function isApplePlatform() {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.userAgentData?.platform || navigator.platform || '';
  if (/Mac|iPhone|iPad|iPod/i.test(platform)) return true;
  // Some browsers report an empty platform; fall back to the UA string.
  return /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

/**
 * True when a keyboard event carries the platform's "send" modifier —
 * ⌘ on Apple, Ctrl elsewhere.
 */
export function hasSendModifier(event) {
  if (!event) return false;
  return event.metaKey || event.ctrlKey;
}
